import { useMutation, useQuery } from "@apollo/client";
import { Fragment, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { MultiValue, SingleValue } from "react-select";
import CreatableSelect from "react-select/creatable";

import {
  ADD_VEHICLESERVICETASK,
  GET_VEHICLESERVICETASKS,
  ServiceTask,
} from "../../graphql/fleets/vehicles/sections/service-tasks";
import { ButtonPopover, PopoverPosition } from "../appearance";
import { selectStyles, SelectWrapper } from ".";

type FieldServiceTaskProps = {
  title: string;
  value: number | null;
  onChange?: (newValue: number | null) => void;
  className?: string;
  isPreview?: boolean;
  position?: PopoverPosition;
};
export function FieldServiceTask({
  title,
  value: initialValue,
  onChange,
  className = "",
  isPreview = false,
  position = PopoverPosition.RIGHT,
}: FieldServiceTaskProps) {
  const [isLoading, setLoading] = useState(false);
  const [value, setValue] = useState<SingleValue<OptionProps>>(null);
  const [createServiceTask] = useMutation(ADD_VEHICLESERVICETASK);

  const { data, loading, refetch } = useQuery<{
    fetchServiceTasks: ServiceTask[];
  }>(GET_VEHICLESERVICETASKS);

  const options: MultiValue<OptionProps> = useMemo(() => {
    if (data?.fetchServiceTasks) {
      return data.fetchServiceTasks?.map((p: ServiceTask) => {
        return {
          label: p.name,
          value: p.id,
        };
      });
    }
    return [];
  }, [data?.fetchServiceTasks]);

  useEffect(() => {
    if (!initialValue) return setValue(null);
    const isInteger = Number.isInteger(initialValue);
    if (isInteger) {
      const option =
        options.find((option) => option.value === String(initialValue)) ?? null;
      setValue(option);
      return;
    }
    setValue(null);
  }, [initialValue, options]);

  const onChangeHandler = (newValue: SingleValue<OptionProps>) => {
    typeof onChange === "function" &&
      onChange(newValue ? Number(newValue.value) : null);
  };

  const handleCreate = (inputValue: string) => {
    setLoading(true);
    createServiceTask({
      variables: {
        name: inputValue,
        status: true,
      },
    })
      .then(({ data }) => {
        setLoading(false);
        if (data?.vehicleServiceTaskCreate) {
          const newValue = data.vehicleServiceTaskCreate.vehicleServiceTask;
          const newOption = {
            label: newValue.name,
            value: newValue.id,
          };
          refetch();
          onChangeHandler(newOption);
        } else {
          toast.error("Error fetching vehicle service task.");
        }
      })
      .catch((error) => {
        setLoading(false);
        toast.error(error.message);
      });
  };

  if (isPreview) {
    if (value) {
      const serviceTask = data?.fetchServiceTasks?.find(
        (ex: ServiceTask) => ex.id === value?.value
      );
      if (serviceTask) {
        return (
          <ButtonPopover
            title={serviceTask.name}
            child={<div>{serviceTask.name}</div>}
            position={position}
          >
            <div className="px-2.5 py-2 md:px-4 md:py-3 xl:px-5 xl:pb-3 xl:pt-5">
              <h3 className="mb-1 font-medium">{serviceTask.name}</h3>
            </div>
          </ButtonPopover>
        );
      }
    }
    return <Fragment>-</Fragment>;
  }

  return (
    <SelectWrapper className={className}>
      <CreatableSelect
        isClearable
        styles={selectStyles}
        isDisabled={isLoading || loading}
        isLoading={isLoading || loading}
        onChange={onChangeHandler}
        onCreateOption={handleCreate}
        options={options}
        value={value}
        onMenuOpen={() => {
          refetch();
        }}
      />
    </SelectWrapper>
  );
}
